import type {
  ARTIFACT_LIBRARY_CHIPS,
  ArtifactLibraryItem,
  ArtifactLibrarySharingId,
} from "@/types/artifactLibrary.ts";

/**
 * The results a Library list renders for the fetched page under a Sharing
 * selection.
 *
 * Only shared items while the filter is narrowed to them. The clients and the
 * backend deploy independently, and a backend older than the Sharing filter
 * strips the `sharing` param it does not know and answers with the caller's own
 * Library — which that filter would otherwise label as shared with them. Failing
 * closed shows the empty state for that window instead. Under `all` a mix is
 * exactly what was asked for, so nothing is filtered.
 */
export const visibleLibraryItems = (
  items: ArtifactLibraryItem[],
  sharingId: ArtifactLibrarySharingId,
): ArtifactLibraryItem[] =>
  sharingId === "with-me" ? items.filter((item) => item.itemKind === "sharedArtifact") : items;

/**
 * What an empty Library result set says.
 *
 * The generic "No documents found" is about a search coming up short. Under the
 * sharing filter the likeliest reason is that nobody has shared anything yet,
 * which is a different sentence — and the chip label alone would render it as
 * "No documents found" for someone who has simply never been sent one.
 */
export const libraryEmptyStateText = (
  sharingId: ArtifactLibrarySharingId,
  chip: (typeof ARTIFACT_LIBRARY_CHIPS)[number],
): string => {
  if (sharingId === "with-me") return "Nothing has been shared with you yet";
  return chip.id === "all" ? "No content found" : `No ${chip.label.toLowerCase()} found`;
};
